import { useEffect, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import PageHeader from '../components/PageHeader';
import { formatDateTime } from '../lib/format';

// طلبات إدارات المؤسسة لمتطوعين. الإدارة بتفتح طلب، HR بتوافق قبل ما
// يبقى مفتوح للتسجيل، وبعدين الطلب بيقفل لما يتحقق العدد المطلوب.

type Row = {
  id: number;
  department: string;
  count: number;
  task_details: string;
  start_date: string | null;
  end_date: string | null;
  time_from: string | null;
  time_to: string | null;
  supervisor: string | null;
  supervisor_phone: string | null;
  requirements: string | null;
  location: string | null;
  address: string | null;
  status: 'PENDING' | 'OPEN' | 'CLOSED' | 'REJECTED';
  hr_note: string | null;
  created_at: string;
};

type Draft = Omit<Row, 'id' | 'created_at' | 'status' | 'hr_note'> & { id?: number; status?: Row['status']; hr_note?: string | null };

const STATUS_LABEL: Record<Row['status'], string> = {
  PENDING: 'بانتظار HR', OPEN: 'مفتوح للتطوع', CLOSED: 'اكتمل', REJECTED: 'مرفوض',
};

const emptyDraft: Draft = {
  department: '',
  count: 1,
  task_details: '',
  start_date: null,
  end_date: null,
  time_from: '',
  time_to: '',
  supervisor: '',
  supervisor_phone: '',
  requirements: '',
  location: '',
  address: '',
};

export default function VolunteeringStaffingPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [statusFilter, setStatusFilter] = useState<'ALL' | Row['status']>('ALL');
  const [editing, setEditing] = useState<Draft | null>(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { void load(); }, []);

  const load = async () => {
    setLoading(true);
    const { data, error: err } = await supabase.from('vol_staffing_requests').select('*').order('created_at', { ascending: false });
    if (err) setError(err.message);
    else setRows((data ?? []) as Row[]);
    setLoading(false);
  };

  const filtered = statusFilter === 'ALL' ? rows : rows.filter((r) => r.status === statusFilter);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const payload = { ...editing };
    if (creating) delete (payload as any).status;
    const { error: err } = creating
      ? await supabase.from('vol_staffing_requests').insert(payload)
      : await supabase.from('vol_staffing_requests').update(payload).eq('id', editing.id!);
    if (err) return setError(err.message);
    setEditing(null);
    setCreating(false);
    await load();
  };

  const setStatus = async (r: Row, status: Row['status']) => {
    const { error: err } = await supabase.from('vol_staffing_requests').update({ status }).eq('id', r.id);
    if (err) setError(err.message);
    else await load();
  };

  return (
    <>
      <PageHeader
        eyebrow="التطوع"
        title="طلبات الإدارات لمتطوعين"
        subtitle="أي إدارة بتطلب متطوعين — HR بتوافق قبل ما الطلب يفتح للتسجيل"
        actions={<button className="btn" onClick={() => { setCreating(true); setEditing({ ...emptyDraft }); }}>+ طلب جديد</button>}
      />
      {error && <div className="error-inline">{error}</div>}

      <div className="card" style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}>
          <option value="ALL">كل الحالات</option>
          <option value="PENDING">بانتظار HR</option>
          <option value="OPEN">مفتوح</option>
          <option value="CLOSED">اكتمل</option>
          <option value="REJECTED">مرفوض</option>
        </select>
        <span className="badge">{filtered.length} من {rows.length}</span>
      </div>

      {loading ? (
        <div className="empty">جاري التحميل...</div>
      ) : filtered.length === 0 ? (
        <div className="card empty">مفيش طلبات بالفلتر ده</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {filtered.map((r) => (
            <div key={r.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div>
                  <strong>{r.department}</strong>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>عدد مطلوب: {r.count}</div>
                </div>
                <span className={`badge ${r.status === 'OPEN' ? 'badge-success' : r.status === 'REJECTED' ? 'badge-danger' : 'badge-pending'}`}>
                  {STATUS_LABEL[r.status]}
                </span>
              </div>
              <div style={{ fontSize: 13, marginTop: 8 }}>{r.task_details}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
                {r.start_date ?? '—'} → {r.end_date ?? '—'} · {r.time_from ?? '—'} إلى {r.time_to ?? '—'}
              </div>
              {r.supervisor && <div style={{ fontSize: 12, marginTop: 4 }}>المسؤول: {r.supervisor} <span style={{ direction: 'ltr' }}>{r.supervisor_phone ?? ''}</span></div>}
              {r.location && <div style={{ fontSize: 12, marginTop: 4 }}>📍 {r.location}</div>}
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                {r.status === 'PENDING' && (
                  <>
                    <button className="btn btn-small" onClick={() => void setStatus(r, 'OPEN')}>موافقة → فتح</button>
                    <button className="btn btn-small btn-danger" onClick={() => void setStatus(r, 'REJECTED')}>رفض</button>
                  </>
                )}
                {r.status === 'OPEN' && (
                  <button className="btn btn-small btn-secondary" onClick={() => void setStatus(r, 'CLOSED')}>غلق (اكتمل العدد)</button>
                )}
                <button className="btn btn-small btn-secondary" onClick={() => { setCreating(false); setEditing({ ...r }); }}>تعديل</button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 8 }}>{formatDateTime(r.created_at)}</div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{creating ? 'طلب جديد' : `تعديل: ${editing.department}`}</h2>
              <button className="btn btn-small btn-secondary" onClick={() => setEditing(null)}>إغلاق</button>
            </div>
            <form onSubmit={save}>
              <div className="row">
                <div className="field"><label>الإدارة الطالبة</label><input required value={editing.department} onChange={(e) => setEditing({ ...editing, department: e.target.value })} /></div>
                <div className="field"><label>العدد</label><input required type="number" min={1} value={editing.count} onChange={(e) => setEditing({ ...editing, count: Number(e.target.value) })} /></div>
              </div>
              <div className="field"><label>تفاصيل المهمة</label><textarea required rows={3} value={editing.task_details} onChange={(e) => setEditing({ ...editing, task_details: e.target.value })} /></div>
              <div className="row">
                <div className="field"><label>من تاريخ</label><input type="date" value={editing.start_date ?? ''} onChange={(e) => setEditing({ ...editing, start_date: e.target.value || null })} /></div>
                <div className="field"><label>إلى تاريخ</label><input type="date" value={editing.end_date ?? ''} onChange={(e) => setEditing({ ...editing, end_date: e.target.value || null })} /></div>
                <div className="field"><label>من</label><input value={editing.time_from ?? ''} onChange={(e) => setEditing({ ...editing, time_from: e.target.value })} /></div>
                <div className="field"><label>إلى</label><input value={editing.time_to ?? ''} onChange={(e) => setEditing({ ...editing, time_to: e.target.value })} /></div>
              </div>
              <div className="row">
                <div className="field"><label>المسؤول</label><input value={editing.supervisor ?? ''} onChange={(e) => setEditing({ ...editing, supervisor: e.target.value })} /></div>
                <div className="field"><label>موبايل المسؤول</label><input value={editing.supervisor_phone ?? ''} onChange={(e) => setEditing({ ...editing, supervisor_phone: e.target.value })} style={{ direction: 'ltr' }} /></div>
              </div>
              <div className="field"><label>شروط المتطوع</label><textarea rows={2} value={editing.requirements ?? ''} onChange={(e) => setEditing({ ...editing, requirements: e.target.value })} /></div>
              <div className="row">
                <div className="field"><label>اللوكيشن</label><input value={editing.location ?? ''} onChange={(e) => setEditing({ ...editing, location: e.target.value })} /></div>
                <div className="field"><label>العنوان</label><input value={editing.address ?? ''} onChange={(e) => setEditing({ ...editing, address: e.target.value })} /></div>
              </div>
              {!creating && (
                <div className="field"><label>ملاحظات HR</label><textarea rows={2} value={editing.hr_note ?? ''} onChange={(e) => setEditing({ ...editing, hr_note: e.target.value })} /></div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>إلغاء</button>
                <button type="submit" className="btn">{creating ? 'إرسال للـ HR' : 'حفظ'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
